import type { NotificationSummary, TextEvent } from '@smarton-pbx/shared';
import { Bell, CheckCheck, Gauge, GraduationCap, Hourglass, MessageSquare, PhoneForwarded, PhoneMissed, RadioTower, ShieldAlert, Voicemail } from 'lucide-react';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../api';
import { EmptyState } from '../components/ui';
import { message, t, tp } from '../i18n';
import { when } from './Calls';

interface NotificationsState {
  items: NotificationSummary[];
  unread: number;
  markRead: (ids: string[] | 'all') => void;
  /** Conversations with unread texts. */
  textsUnread: number;
  /** Calls back whenever a conversation the person sees changes; returns how to stop. */
  onText: (listener: (event: TextEvent) => void) => () => void;
}

const NotificationsContext = createContext<NotificationsState>({
  items: [],
  unread: 0,
  markRead: () => undefined,
  textsUnread: 0,
  onText: () => () => undefined,
});

export function useNotifications(): NotificationsState {
  return useContext(NotificationsContext);
}

/**
 * The in-app notification list, kept current over a server-sent event
 * stream, and the texts' unread count over the same stream. Also follows the
 * service worker's "open this page" requests.
 */
export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<NotificationSummary[]>([]);
  const [unread, setUnread] = useState(0);
  const [textsUnread, setTextsUnread] = useState(0);
  const textListeners = useRef(new Set<(event: TextEvent) => void>());
  const navigate = useNavigate();

  useEffect(() => {
    let stopped = false;
    void api.listNotifications().then((list) => {
      if (stopped) return;
      setItems(list.items);
      setUnread(list.unread);
    });
    void api.textUnread().then((result) => !stopped && setTextsUnread(result.unread));
    const stream = new EventSource(api.notificationStreamUrl());
    stream.addEventListener('notification', (event) => {
      const data = JSON.parse((event as MessageEvent<string>).data) as { notification: NotificationSummary; unread: number };
      setItems((list) => [data.notification, ...list.filter((n) => n.id !== data.notification.id)].slice(0, 50));
      setUnread(data.unread);
    });
    stream.addEventListener('unread', (event) => {
      setUnread((JSON.parse((event as MessageEvent<string>).data) as { unread: number }).unread);
    });
    stream.addEventListener('text', (event) => {
      const data = JSON.parse((event as MessageEvent<string>).data) as TextEvent;
      setTextsUnread(data.unread);
      for (const listener of textListeners.current) listener(data);
    });
    return () => {
      stopped = true;
      stream.close();
    };
  }, []);

  // A notification clicked while the app is open: go where it points.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (event: MessageEvent<{ type?: string; url?: string }>) => {
      if (event.data?.type === 'open' && event.data.url?.startsWith('/')) void navigate(event.data.url);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  const markRead = useCallback((ids: string[] | 'all') => {
    setItems((list) => list.map((n) => (ids === 'all' || ids.includes(n.id) ? { ...n, read: true } : n)));
    void api.markNotificationsRead(ids).then((result) => setUnread(result.unread));
  }, []);

  const onText = useCallback((listener: (event: TextEvent) => void) => {
    textListeners.current.add(listener);
    return () => {
      textListeners.current.delete(listener);
    };
  }, []);

  return <NotificationsContext.Provider value={{ items, unread, markRead, textsUnread, onText }}>{children}</NotificationsContext.Provider>;
}

const icons = {
  'missed-call': PhoneMissed,
  voicemail: Voicemail,
  text: MessageSquare,
  'call-blocked': ShieldAlert,
  'trunk-down': RadioTower,
  'incoming-call': Bell,
  'weekly-digest': GraduationCap,
  'ai-budget-reached': Gauge,
  'queue-callback': PhoneForwarded,
  'queue-waiting': Hourglass,
} as const;

/** The bell with the unread count, and the list it opens. */
export function NotificationBell() {
  const { items, unread, markRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="notifications-anchor" ref={ref}>
      <button
        type="button"
        className="ghost icon bell"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={unread ? tp(unread, 'Notifications, {n} unread', 'Notifications, {n} unread') : t('Notifications')}
        data-testid="notification-bell"
      >
        <Bell size={19} aria-hidden />
        {unread > 0 && (
          <span className="badge" data-testid="notification-count">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="notifications-panel" role="dialog" aria-label={t('Notifications')} data-testid="notification-panel">
          <div className="notifications-head">
            <strong>{t('Notifications')}</strong>
            {unread > 0 && (
              <button type="button" className="ghost small" onClick={() => markRead('all')}>
                <CheckCheck size={15} aria-hidden /> {t('Mark all read')}
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <EmptyState icon={Bell} title={t('Nothing yet')}>
              <p>{t('Missed calls and new voicemail appear here.')}</p>
            </EmptyState>
          ) : (
            <ul className="notifications-list">
              {items.map((item) => {
                const Icon = icons[item.kind] ?? Bell;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={item.read ? '' : 'unread'}
                      onClick={() => {
                        if (!item.read) markRead([item.id]);
                        setOpen(false);
                        if (item.link) void navigate(item.link);
                      }}
                      data-testid="notification-item"
                    >
                      <span className={`notification-icon is-${item.kind}`} aria-hidden>
                        <Icon size={16} />
                      </span>
                      <span className="grow">
                        <span className="notification-title">{message(item.title)}</span>
                        {item.body && <span className="notification-body">{message(item.body)}</span>}
                        <span className="notification-time">{when(item.createdAt)}</span>
                      </span>
                      {!item.read && <span className="unread-dot" aria-label={t('Unread')} />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
